import React, { useMemo, useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  type CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CountrySelect,
  Form,
  FormField,
  IconAddCandidateOutline,
  IconPlus,
  SlideIn,
} from '@/components';
import { EmailTemplateSelect } from '@/components/EmailTemplateSelect';
import { CircularIconContainer } from '@/components/Icons/IconContainer/';
import {
  type CandidateBasicDto,
  InterviewStatus,
  InterviewType,
  type InviteCandidateRequest,
  type MailTemplateDto,
} from '@/services/api';
import { splitNameAndSurname } from '@/utils/identity';

import {
  type FormFieldsCandidateAdd,
  zFormFieldsCandidateAdd,
} from '../admin-interview/schemas';
import {
  adminInterviewKeys,
  useAddTemplateToInterviewMutation,
  useCandidatesAddMutation,
  useEditCandidateRequestMutation,
  useGetInterviewQuery,
} from '../admin-interview/useAdminInterviewQueries';
import { useIsTemplateInterview } from '../admin-interview/useIsTemplateInterview';
import { mergeDateAndTime } from '../admin-new-interview';

interface AddNewCandidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId: string;
  isEdit: boolean;
  candidate?: CandidateBasicDto;
  editingCandidateInfo?: {
    fullName: string;
    email: string;
    phoneNumber?: string;
  };
}

export const AddOrEditCandidateModal: React.FC<AddNewCandidateModalProps> = ({
  isOpen,
  onClose,
  interviewId,
  isEdit,
  candidate,
  editingCandidateInfo,
}) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'candidate', 'auth']);
  const [inviteWithMail, setInviteWithMail] = useState(true);
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<MailTemplateDto['id']>();
  const [country, setCountry] = useState<CountryCode>();
  const isTemplate = useIsTemplateInterview();

  const createInterviewFromTemplate = async () => {
    await addTemplateToInterview.mutateAsync(interviewId);
    const url = new URL(window.location.href);
    url.searchParams.delete('isTemplate');
    window.history.replaceState({}, '', url.toString());
  };

  const addTemplateToInterview = useAddTemplateToInterviewMutation();
  const CandidateAdd = useCandidatesAddMutation(
    interviewId,
    selectedTemplateId,
    {
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: adminInterviewKeys.getInterview({
            interviewsId: interviewId,
          }),
        });
        toast({
          title:
            inviteWithMail === true
              ? t('candidate:inviteNewCandidate.newCandidateInvited')
              : t('candidate:inviteNewCandidate.newCandidateAdded'),
          status: 'success',
        });
        form.reset();
        onClose();
      },
    }
  );
  const isCandidateCompleted = candidate?.status === InterviewStatus.Completed;

  const { data: interviewData } = useGetInterviewQuery({
    interviewsId: interviewId,
  });

  const isInterviewAssistant = useMemo(
    () => interviewData?.interview?.type === InterviewType.InterviewAssistant,
    [interviewData]
  );

  const candidateEditMutation = useEditCandidateRequestMutation({
    onSuccess() {
      form.reset();
      onClose();
    },
  });

  type FormInputs = {
    fullName?: string;
    email?: string;
    sendInvitationMail?: boolean;
    phoneNumber?: string;
    turkishIdNumber?: string;
    link?: string;
    startDate?: Date;
    startTime?: string;
  };
  const form = useForm<FormFieldsCandidateAdd>({
    mode: 'onBlur',
    resolver: zodResolver(zFormFieldsCandidateAdd(isInterviewAssistant)),
    defaultValues: {
      fullName: '',
      email: '',
      phoneNumber: '',
      turkishIdNumber: '',
      sendInvitationMail: !isInterviewAssistant,
    },
    values: editingCandidateInfo
      ? {
          ...editingCandidateInfo,
          phoneNumber:
            editingCandidateInfo.phoneNumber &&
            editingCandidateInfo.phoneNumber.trim() !== ''
              ? (parsePhoneNumberFromString(
                  editingCandidateInfo.phoneNumber,
                  country ?? 'TR'
                )?.format('E.164') ?? '')
              : '',
          sendInvitationMail: isCandidateCompleted ? false : true,
        }
      : undefined,
  });

  const {
    register,
    formState: { errors },
  } = form;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { firstName, surname } = splitNameAndSurname(
      data.fullName?.trim() ?? ''
    );

    const candidateData: InviteCandidateRequest = {
      name: firstName,
      surname,
      email: data.email?.trim(),
      sendInvitationMail: data.sendInvitationMail,
      mailTemplateId: selectedTemplateId,
      phone: data.phoneNumber
        ? (parsePhoneNumberFromString(
            data.phoneNumber,
            country ?? 'TR'
          )?.format('E.164') ?? undefined)
        : undefined,
      identityNumber: data.turkishIdNumber,
      ...(isInterviewAssistant && {
        meetingLink: data.link,
        meetingDate: mergeDateAndTime(data.startDate!, data.startTime!),
      }),
    };

    setInviteWithMail(data.sendInvitationMail ?? false);

    if (isEdit) {
      if (!candidate?.id) return;

      await candidateEditMutation.mutateAsync({
        interviewsId: interviewId,
        candidateId: candidate?.id,
        body: {
          ...candidateData,
          phone: candidateData.phone ?? '',
        },
      });
    } else {
      if (isTemplate) await createInterviewFromTemplate();
      await CandidateAdd.mutateAsync(candidateData);
    }
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        form.reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader justifyItems="center">
          <VStack>
            <CircularIconContainer icon={IconAddCandidateOutline} />
            <Text>
              {isEdit
                ? t('candidate:inviteNewCandidate.editCandidate')
                : t('candidate:inviteNewCandidate.addNewCandidate')}
            </Text>
            {!isEdit && (
              <Text fontSize="sm" fontWeight="light">
                {t('candidate:inviteNewCandidate.enterCandidateInfo')}
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalBody w="full" pb={2}>
          <SlideIn>
            <Form {...form} onSubmit={onSubmit}>
              <Stack spacing={4}>
                <FormField
                  placeholder={t('common:information.fullName')}
                  type="text"
                  control={form.control}
                  name="fullName"
                  size="lg"
                />

                <FormField
                  placeholder={t('common:information.email')}
                  type="email"
                  control={form.control}
                  name="email"
                  size="lg"
                />

                <Flex columnGap={3} alignItems="center">
                  <Stack minWidth={100} rowGap={0.5}>
                    <CountrySelect
                      value={country}
                      onCountryChange={setCountry as ExplicitAny}
                    />
                  </Stack>

                  <FormField
                    type="phone-number"
                    name="phoneNumber"
                    control={form.control}
                    placeholder={t('common:information.phonePlaceholder')}
                    country={country}
                    withCountryCallingCode={false}
                    international
                    useNationalFormatForDefaultCountryValue
                  />
                </Flex>
                <FormField
                  type="text"
                  name="turkishIdNumber"
                  control={form.control}
                  placeholder={t(
                    'common:information.turkishIdNumberPlaceholder'
                  )}
                />

                {!isInterviewAssistant && (
                  <>
                    <EmailTemplateSelect
                      selectedTemplateId={selectedTemplateId}
                      setSelectedTemplateId={setSelectedTemplateId}
                      isDisabled={isCandidateCompleted}
                    />

                    <FormField
                      type="custom"
                      control={form.control}
                      name="sendInvitationMail"
                      render={({ field: { value, ...rest } }) => (
                        <HStack>
                          <Switch
                            checked={value}
                            colorScheme="brand"
                            defaultChecked={!isCandidateCompleted}
                            size="lg"
                            isDisabled={isCandidateCompleted}
                            {...rest}
                          />
                          <Text fontWeight="medium">
                            {t('candidate:inviteNewCandidate.sendInvitation')}
                          </Text>
                        </HStack>
                      )}
                    />
                  </>
                )}

                {isInterviewAssistant && (
                  <>
                    <FormField
                      placeholder={t('common:information.link')}
                      type="text"
                      control={form.control}
                      name="link"
                      size="lg"
                    />
                    <HStack spacing={4} mt={5}>
                      <FormField
                        type="date"
                        dayPickerProps={{
                          arePastDaysDisabled: true,
                          placeholder: t('common:information.meetingDate'),
                        }}
                        rules={{
                          required: t('auth:users.data.meetingDate.required'),
                        }}
                        control={form.control}
                        name="startDate"
                      />

                      <FormControl isInvalid={!!errors.startTime}>
                        <Input
                          id="startTime"
                          type="time"
                          {...register('startTime', {
                            required: t('auth:users.data.meetingTime.required'),
                          })}
                        />
                        <FormErrorMessage>
                          {errors.startTime && errors.startTime.message}
                        </FormErrorMessage>
                      </FormControl>
                    </HStack>
                  </>
                )}
                <Button
                  type="submit"
                  mb={8}
                  mt={4}
                  isLoading={
                    CandidateAdd.isPending ||
                    candidateEditMutation.isPending ||
                    addTemplateToInterview.isPending
                  }
                  leftIcon={isEdit ? undefined : <IconPlus />}
                  variant="@primary"
                  w="full"
                >
                  {isEdit
                    ? t('actions.update')
                    : t('candidate:inviteNewCandidate.addCandidate')}
                </Button>
              </Stack>
            </Form>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
